import { useState, useEffect } from 'react';

interface FilterConfig {
  showAcctgDt?: boolean;
  showPlanningArea?: boolean;
  showTechLdap?: boolean;
  showQuotedJobcode?: boolean;
  showDeclineReason?: boolean;
  showLlmClassification?: boolean;
  showCreateSource?: boolean;
  showApplianceType?: boolean;
  showCustomerRating?: boolean;
}

interface FilterOptions {
  areas: string[];
  jobcodes: string[];
  techLdaps: string[];
  acctgDates: string[];
  declineReasons: string[];
  applianceTypes: string[];
  createSources: string[];
  llmChatClassifications: string[];
  customerRatings: string[];
  loading: boolean;
}

/**
 * Custom hook for fetching filter dropdown options
 * Automatically cancels requests on unmount or config change
 */
export const useFilterOptions = (config: FilterConfig): FilterOptions => {
  const [areas, setAreas] = useState<string[]>([]);
  const [jobcodes, setJobcodes] = useState<string[]>([]);
  const [techLdaps, setTechLdaps] = useState<string[]>([]);
  const [acctgDates, setAcctgDates] = useState<string[]>([]);
  const [declineReasons, setDeclineReasons] = useState<string[]>([]);
  const [applianceTypes, setApplianceTypes] = useState<string[]>([]);
  const [createSources, setCreateSources] = useState<string[]>([]);
  const [llmChatClassifications, setLlmChatClassifications] = useState<string[]>([]);
  const [customerRatings, setCustomerRatings] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;
    const abortController = new AbortController();

    (async () => {
      try {
        setLoading(true);
        const fetchRequests: Promise<Response>[] = [];
        const fetchKeys: string[] = [];

        if (config.showPlanningArea) {
          fetchRequests.push(fetch("api/snowflake/planning-areas", { signal: abortController.signal }));
          fetchKeys.push("areas");
        }
        if (config.showQuotedJobcode) {
          fetchRequests.push(fetch("api/snowflake/quoted-jobcodes", { signal: abortController.signal }));
          fetchKeys.push("jobcodes");
        }
        if (config.showTechLdap) {
          fetchRequests.push(fetch("api/snowflake/tech-ldaps", { signal: abortController.signal }));
          fetchKeys.push("ldaps");
        }
        if (config.showAcctgDt) {
          fetchRequests.push(fetch("api/snowflake/acctg-dates", { signal: abortController.signal }));
          fetchKeys.push("acctgDates");
        }
        if (config.showDeclineReason) {
          fetchRequests.push(fetch("api/snowflake/decline-reasons", { signal: abortController.signal }));
          fetchKeys.push("declineReasons");
        }
        if (config.showApplianceType) {
          fetchRequests.push(fetch("api/snowflake/appliance-types", { signal: abortController.signal }));
          fetchKeys.push("applianceTypes");
        }
        if (config.showCreateSource) {
          fetchRequests.push(fetch("api/snowflake/create-sources", { signal: abortController.signal }));
          fetchKeys.push("createSources");
        }
        if (config.showLlmClassification) {
          fetchRequests.push(fetch("api/snowflake/llm-chat-classifications", { signal: abortController.signal }));
          fetchKeys.push("llmChatClassifications");
        }
        if (config.showCustomerRating) {
          fetchRequests.push(fetch("api/snowflake/customer-ratings", { signal: abortController.signal }));
          fetchKeys.push("customerRatings");
        }

        const responses = await Promise.all(fetchRequests);
        const jsonData = await Promise.all(responses.map((r) => r.json()));

        if (mounted) {
          jsonData.forEach((data, index) => {
            const key = fetchKeys[index];
            switch (key) {
              case "areas":
                if (Array.isArray(data?.areas)) setAreas(data.areas);
                break;
              case "jobcodes":
                if (Array.isArray(data?.jobcodes)) setJobcodes(data.jobcodes);
                break;
              case "ldaps":
                if (Array.isArray(data?.ldaps)) setTechLdaps(data.ldaps);
                break;
              case "acctgDates":
                if (Array.isArray(data?.acctgDates)) setAcctgDates(data.acctgDates);
                break;
              case "declineReasons":
                if (Array.isArray(data?.declineReasons)) setDeclineReasons(data.declineReasons);
                break;
              case "applianceTypes":
                if (Array.isArray(data?.applianceTypes)) setApplianceTypes(data.applianceTypes);
                break;
              case "createSources":
                if (Array.isArray(data?.createSources)) setCreateSources(data.createSources);
                break;
              case "llmChatClassifications":
                if (Array.isArray(data?.llmChatClassifications)) setLlmChatClassifications(data.llmChatClassifications);
                break;
              case "customerRatings":
                if (Array.isArray(data?.customerRatings)) setCustomerRatings(data.customerRatings);
                break;
            }
          });
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          console.error("Error fetching filter options:", e);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, [
    config.showPlanningArea,
    config.showQuotedJobcode,
    config.showTechLdap,
    config.showAcctgDt,
    config.showDeclineReason,
    config.showApplianceType,
    config.showCreateSource,
    config.showLlmClassification,
    config.showCustomerRating,
  ]);

  return {
    areas,
    jobcodes,
    techLdaps,
    acctgDates,
    declineReasons,
    applianceTypes,
    createSources,
    llmChatClassifications,
    customerRatings,
    loading,
  };
};
